import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { LEVELS, DAYS, formatTime, formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileUpload } from "@/components/FileUpload";
import { Plus, Pencil, CalendarRange, Trash2 } from "lucide-react";

const EMPTY = { name: "", level: "Beginner", venue_id: "", day: "Monday", start_time: "19:00", finish_time: "20:30", price: 4, description: "", temporary_notice: "", booking_required: false, active: true, contact_email: "", photo_path: "" };

function TermsDialog({ cls, open, onOpenChange }) {
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [editId, setEditId] = useState(null);
  const { data: terms, refetch, isLoading } = useQuery({
    queryKey: ["terms", cls?.id], enabled: !!cls && open,
    queryFn: async () => (await api.get(`/admin/classes/${cls.id}/terms`)).data,
  });
  const reset = () => { setName(""); setStart(""); setEditId(null); };
  const save = async () => {
    if (!name || !start) { toast.error("Enter a course name and start date"); return; }
    try {
      if (editId) await api.put(`/admin/terms/${editId}`, { name, start_date: start });
      else await api.post(`/admin/classes/${cls.id}/terms`, { name, start_date: start });
      toast.success(editId ? "Term updated" : "New term started"); reset(); refetch();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (t) => { if (!window.confirm(`Delete term "${t.name}"? Dances taught aren't deleted, only this course grouping.`)) return; await api.delete(`/admin/terms/${t.id}`); refetch(); };
  const sorted = [...(terms || [])].sort((a, b) => b.start_date.localeCompare(a.start_date));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Course terms — {cls?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5C4A42]">Each term is a run of the course (e.g. a 12-week block). The newest term is the <strong>current course</strong> shown to dancers; older ones move to “Past courses”. Starting a new term automatically archives the previous one.</p>
        <div className="rounded-xl border border-border bg-secondary/40 p-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Course name</label><input className={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Autumn 2026" data-testid="term-name" /></div>
            <div><label className={lbl}>Start date</label><input className={inp} type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="term-start" /></div>
          </div>
          <div className="mt-3 flex gap-2">
            <button className={btn} onClick={save} data-testid="term-save">{editId ? "Update term" : "Start new term"}</button>
            {editId && <button className={btnGhost} onClick={reset}>Cancel edit</button>}
          </div>
        </div>
        <div className="space-y-2">
          {isLoading ? <Spinner /> : sorted.length === 0 ? (
            <p className="text-sm text-[#6E5B51]">No terms yet. Start one to archive the current course and begin a fresh run.</p>
          ) : sorted.map((t, i) => (
            <div key={t.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-3" data-testid={`term-row-${t.id}`}>
              <div>
                <p className="font-semibold text-[#2D1E18]">{t.name} {i === 0 && <span className="ml-1 rounded-full bg-[#C85A32] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Current</span>}</p>
                <p className="text-sm text-[#6E5B51]">Starts {formatDate(t.start_date, { year: true })}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(t.id); setName(t.name); setStart(t.start_date); }} className="rounded-lg p-1.5 text-[#5C4A42] hover:bg-secondary" aria-label="Edit term"><Pencil size={15} /></button>
                <button onClick={() => del(t)} className="rounded-lg p-1.5 text-[#6E5B51] hover:text-[#991B1B]" aria-label="Delete term"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><button className={btnGhost} onClick={() => onOpenChange(false)}>Close</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ManageClasses() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [termsCls, setTermsCls] = useState(null);
  const { data: classes, isLoading } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes?active=false")).data });
  const { data: venues } = useQuery({ queryKey: ["venues"], queryFn: async () => (await api.get("/venues")).data });
  const { data: instructors } = useQuery({ queryKey: ["admin-instructors"], queryFn: async () => (await api.get("/admin/instructors")).data });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleInstructor = (id) => setF((prev) => { const cur = prev.instructor_ids || []; return { ...prev, instructor_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });

  const openNew = () => { setF({ ...EMPTY, venue_id: venues?.[0]?.id || "" }); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setF({ ...c, venue_id: c.venue_id }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...f, price: Number(f.price) };
      delete payload.venue; delete payload.history; delete payload.this_week; delete payload.tonights_playlist; delete payload.instructor; delete payload.instructors; delete payload.current_term; delete payload.past_terms;
      if (editId) await api.put(`/admin/classes/${editId}`, payload); else await api.post("/admin/classes", payload);
      qc.invalidateQueries({ queryKey: ["classes"] }); setOpen(false); toast.success("Class saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout role={user?.role} title="Classes" subtitle="Your weekly class schedule."
      actions={<button className={btn} onClick={openNew} data-testid="add-class-button"><Plus size={16} /> Add class</button>}>
      {isLoading ? <Spinner /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {classes.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-class-${c.slug}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><h3 className="font-display font-bold text-[#2D1E18]">{c.name}</h3><LevelBadge level={c.level} /></div>
                <div className="flex gap-1">
                  <button onClick={() => setTermsCls(c)} className="rounded-lg p-1.5 text-[#5C4A42] hover:bg-secondary" aria-label="Course terms" data-testid={`class-terms-${c.slug}`}><CalendarRange size={15} /></button>
                  <button onClick={() => openEdit(c)} className="rounded-lg p-1.5 text-[#5C4A42] hover:bg-secondary" aria-label="Edit"><Pencil size={15} /></button>
                </div>
              </div>
              <p className="mt-1 text-sm text-[#5C4A42]">{c.day} · {formatTime(c.start_time)}–{formatTime(c.finish_time)} · {c.venue?.name}</p>
              {!c.active && <span className="mt-2 inline-block rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-[#6E5B51]">Inactive</span>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit class" : "Add class"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className={lbl}>Name</label><input className={inp} value={f.name} onChange={set("name")} data-testid="class-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Level</label><select className={inp} value={f.level} onChange={set("level")}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</select></div>
              <div><label className={lbl}>Venue</label><select className={inp} value={f.venue_id} onChange={set("venue_id")} data-testid="class-form-venue">{(venues || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
              <div><label className={lbl}>Day</label><select className={inp} value={f.day} onChange={set("day")}>{DAYS.map((d) => <option key={d}>{d}</option>)}</select></div>
              <div><label className={lbl}>Price (£)</label><input className={inp} type="number" step="0.5" value={f.price} onChange={set("price")} /></div>
              <div><label className={lbl}>Start</label><input className={inp} type="time" value={f.start_time} onChange={set("start_time")} /></div>
              <div><label className={lbl}>Finish</label><input className={inp} type="time" value={f.finish_time} onChange={set("finish_time")} /></div>
            </div>
            <div><label className={lbl}>Description</label><textarea className={inp} rows={2} value={f.description} onChange={set("description")} /></div>
            <div><label className={lbl}>Temporary notice (optional)</label><input className={inp} value={f.temporary_notice} onChange={set("temporary_notice")} /></div>
            <FileUpload label="Class photo (optional)" value={f.photo_path} onChange={(path) => setF({ ...f, photo_path: path })} accept=".png,.jpg,.jpeg,.webp" kind="image" testid="class-photo-upload" />
            <div>
              <label className={lbl}>Instructors</label>
              {!instructors?.length ? (
                <p className="text-sm text-[#6E5B51]">No instructors yet — add them under the Instructors tab first.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="class-instructors-picker">
                  {instructors.map((it) => (
                    <label key={it.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm ${(f.instructor_ids || []).includes(it.id) ? "border-[#C85A32] bg-[#FBEBE4]" : "border-border"}`}>
                      <input type="checkbox" checked={(f.instructor_ids || []).includes(it.id)} onChange={() => toggleInstructor(it.id)} className="h-4 w-4 accent-[#C85A32]" data-testid={`class-instructor-opt-${it.id}`} />
                      <span className="text-[#2D1E18]">{it.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><button className={btnGhost} onClick={() => setOpen(false)}>Cancel</button><button className={btn} onClick={save} data-testid="class-form-save">Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
      <TermsDialog cls={termsCls} open={!!termsCls} onOpenChange={(v) => !v && setTermsCls(null)} />
    </AdminLayout>
  );
}
